import React, { useState } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Divider,
  Stack,
  List,
  ListItem,
  ListItemText,
  Button,
  Chip,
  TextField,
  Alert,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { authService } from '../../services/api';
import LanguageSelector from '../../components/LanguageSelector';
import ColorSchemeSelector from '../../components/ColorSchemeSelector';
import SubPageLayout from '../../components/SubPageLayout';
import ProfileSummaryCard from '../../components/ProfileSummaryCard';

let googleIdentityServicesPromise;

const loadGoogleIdentityServices = () => {
  if (window.google?.accounts?.oauth2) {
    return Promise.resolve(window.google);
  }

  if (!googleIdentityServicesPromise) {
    googleIdentityServicesPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.onload = () => {
        if (window.google?.accounts?.oauth2) {
          resolve(window.google);
          return;
        }
        googleIdentityServicesPromise = null;
        reject(new Error('google_identity_services_load_failed'));
      };
      script.onerror = () => {
        googleIdentityServicesPromise = null;
        reject(new Error('google_identity_services_load_failed'));
      };
      document.head.appendChild(script);
    });
  }

  return googleIdentityServicesPromise;
};

const requestGoogleAccessToken = async () => {
  const clientId = process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID;
  if (!clientId) {
    throw new Error('google_client_id_missing');
  }

  const google = await loadGoogleIdentityServices();
  return new Promise((resolve, reject) => {
    const tokenClient = google.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: 'openid email profile',
      callback: (response) => {
        if (response.error || !response.access_token) {
          reject(new Error('google_popup_failed'));
          return;
        }
        resolve(response.access_token);
      },
      error_callback: () => reject(new Error('google_popup_failed')),
    });
    tokenClient.requestAccessToken();
  });
};

const googleErrorMessage = (error, t) => {
  const detail = error.response?.data?.detail;
  if (Array.isArray(detail)) return detail.join(' ');
  if (typeof detail === 'string') return detail;

  if (error.message === 'google_client_id_missing') return t('profile.google_client_id_missing');
  if (error.message === 'google_identity_services_load_failed') return t('profile.google_script_load_error');
  if (error.message === 'google_popup_failed') return t('profile.google_popup_error');
  return t('profile.google_action_error');
};

// Replaces ListItem's `secondaryAction` (absolutely positioned, fixed-width
// reserved space) with a wrapping flex row, so a long action label — e.g. the
// Google disconnect button — never overlaps the text: it drops below instead.
const ListItemRow = ({
  divider, primary, secondary, action, primaryTypographyProps, secondaryTypographyProps,
}) => (
  <ListItem disableGutters divider={divider} sx={{ display: 'block', py: 1.5 }}>
    <Stack direction="row" flexWrap="wrap" useFlexGap columnGap={2} rowGap={0.5}>
      <ListItemText
        primary={primary}
        secondary={secondary}
        primaryTypographyProps={primaryTypographyProps}
        secondaryTypographyProps={secondaryTypographyProps}
        sx={{ flex: '1 1 200px', minWidth: 0, my: 0 }}
      />
      <Box sx={{ flexShrink: 0, ml: 'auto', alignSelf: 'center' }}>{action}</Box>
    </Stack>
  </ListItem>
);

const MyProfile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { collaborator, updateCollaborator } = useAuth();
  const queryClient = useQueryClient();

  const userName = collaborator?.name || 'Usuário';
  const userEmail = collaborator?.email || '';

  const [editingName, setEditingName] = useState(false);
  const [draft, setDraft] = useState(userName);
  const [googleError, setGoogleError] = useState(null);
  const invalid = draft.trim().length < 3;

  const googleConnectionQuery = useQuery({
    queryKey: ['googleConnection'],
    queryFn: () => authService.getUser().then((response) => response.data),
  });

  const updateNameMutation = useMutation({
    mutationFn: (name) => authService.updateCollaboratorMe({ name }),
    onSuccess: (response) => {
      updateCollaborator(response.data);
      setEditingName(false);
    },
  });

  const connectGoogleMutation = useMutation({
    mutationFn: async () => authService.connectGoogle(await requestGoogleAccessToken()),
    onSuccess: () => {
      queryClient.setQueryData(['googleConnection'], (user) => ({ ...user, google_connected: true }));
      setGoogleError(null);
    },
    onError: (error) => setGoogleError(googleErrorMessage(error, t)),
  });

  const disconnectGoogleMutation = useMutation({
    mutationFn: () => authService.disconnectGoogle(),
    onSuccess: () => {
      queryClient.setQueryData(['googleConnection'], (user) => ({ ...user, google_connected: false }));
      setGoogleError(null);
    },
    onError: (error) => setGoogleError(googleErrorMessage(error, t)),
  });

  const startEditingName = () => {
    setDraft(userName);
    updateNameMutation.reset();
    setEditingName(true);
  };

  const saveName = () => {
    if (invalid) return;
    updateNameMutation.mutate(draft.trim());
  };

  const cancelEditingName = () => {
    setDraft(userName);
    updateNameMutation.reset();
    setEditingName(false);
  };

  const googleConnected = googleConnectionQuery.data?.google_connected;
  const googleActionPending = connectGoogleMutation.isPending || disconnectGoogleMutation.isPending;
  const handleGoogleAction = () => {
    setGoogleError(null);
    if (googleConnected) {
      disconnectGoogleMutation.mutate();
      return;
    }
    connectGoogleMutation.mutate();
  };

  return (
    <SubPageLayout title={t('profile.title')} onBack={() => navigate(location.state?.from || '/settings')}>
      <Stack spacing={3}>
        <ProfileSummaryCard name={userName} email={userEmail} role={collaborator?.role} />

        <Card>
          <CardHeader
            title={t('profile.information')}
            subheader={t('profile.information_subtitle')}
            titleTypographyProps={{ variant: 'h6' }}
          />
          <Divider />
          <CardContent sx={{ py: 0 }}>
            {updateNameMutation.isError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {t('profile.name_update_error')}
              </Alert>
            )}
            <List disablePadding>
              {editingName ? (
                <ListItem disableGutters divider sx={{ display: 'block', py: 2 }}>
                  <Stack spacing={1.5}>
                    <TextField
                      autoFocus
                      fullWidth
                      label={t('profile.name')}
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !invalid) saveName();
                        if (e.key === 'Escape') cancelEditingName();
                      }}
                      disabled={updateNameMutation.isPending}
                      error={invalid}
                    />
                    <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
                      <Typography variant="caption" color={invalid ? 'error' : 'text.secondary'}>
                        {invalid ? t('profile.name_edit_error') : t('profile.name_edit_hint')}
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        <Button color="primary" onClick={cancelEditingName} disabled={updateNameMutation.isPending}>
                          {t('profile.cancel')}
                        </Button>
                        <Button
                          variant="outlined"
                          disabled={invalid || updateNameMutation.isPending}
                          onClick={saveName}
                        >
                          {t('profile.save')}
                        </Button>
                      </Stack>
                    </Stack>
                  </Stack>
                </ListItem>
              ) : (
                <ListItemRow
                  divider
                  primary={t('profile.name')}
                  secondary={userName}
                  primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                  secondaryTypographyProps={{ variant: 'body1', color: 'text.primary' }}
                  action={
                    <Button variant="outlined" size="small" onClick={startEditingName}>
                      {t('profile.edit')}
                    </Button>
                  }
                />
              )}
              <ListItem disableGutters>
                <ListItemText
                  primary={t('profile.email')}
                  secondary={userEmail}
                  primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                  secondaryTypographyProps={{ variant: 'body1', color: 'text.primary' }}
                />
              </ListItem>
            </List>
          </CardContent>
        </Card>

        <Card>
          <CardHeader
            title={t('profile.google_account')}
            subheader={t('profile.google_account_subtitle')}
            titleTypographyProps={{ variant: 'h6' }}
            action={
              googleConnected !== undefined && (
                <Chip
                  size="small"
                  color={googleConnected ? 'success' : 'default'}
                  variant={googleConnected ? 'filled' : 'outlined'}
                  label={t(googleConnected ? 'profile.google_connected' : 'profile.google_not_connected')}
                />
              )
            }
          />
          <Divider />
          <CardContent sx={{ py: 0 }}>
            <List disablePadding>
              <ListItemRow
                primary={t('profile.google_account')}
                secondary={
                  googleConnectionQuery.isPending
                    ? t('profile.google_loading')
                    : googleConnected
                      ? t('profile.google_connected_hint')
                      : t('profile.google_not_connected_hint')
                }
                action={
                  <Button
                    variant="outlined"
                    color={googleConnected ? 'error' : 'primary'}
                    size="small"
                    onClick={handleGoogleAction}
                    disabled={googleConnectionQuery.isPending || googleConnectionQuery.isError || googleActionPending}
                  >
                    {t(googleConnected ? 'profile.google_disconnect' : 'profile.google_connect')}
                  </Button>
                }
              />
            </List>
            {(googleError || googleConnectionQuery.isError) && (
              <Alert severity="error" sx={{ my: 2 }}>
                {googleError || t('profile.google_state_error')}
              </Alert>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader
            title={t('profile.preferences')}
            subheader={t('profile.preferences_subtitle')}
            titleTypographyProps={{ variant: 'h6' }}
          />
          <Divider />
          <CardContent sx={{ py: 0 }}>
            <List disablePadding>
              <ListItemRow
                divider
                primary={t('profile.language')}
                secondary={t('profile.language_hint')}
                action={<LanguageSelector variant="select" />}
              />
              <ListItemRow
                divider
                primary={t('profile.color_scheme')}
                secondary={t('profile.color_scheme_hint')}
                action={<ColorSchemeSelector variant="select" />}
              />
              <ListItemRow
                primary={t('notifications.title')}
                secondary={t('profile.notifications_hint')}
                action={
                  <Button
                    variant="outlined"
                    size="small"
                    endIcon={<ChevronRightIcon />}
                    onClick={() => navigate('/settings/notifications', { state: { from: location.pathname } })}
                  >
                    {t('profile.manage')}
                  </Button>
                }
              />
            </List>
          </CardContent>
        </Card>

        <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" color="text.secondary">
          <CheckIcon fontSize="small" />
          <Typography variant="body2">{t('profile.footer')}</Typography>
        </Stack>
      </Stack>
    </SubPageLayout>
  );
};

export default MyProfile;
